import React, { useContext, useState } from "react";
import {
  makeStyles,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import VisibilityIcon from "@material-ui/icons/Visibility";
import LayersIcon from "@material-ui/icons/Layers";
import AuthContext from "connections/auth/AuthProvider.js";
import { useTranslation } from "react-i18next";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import {
  ProgramDataProvider,
  useProgramDataContext,
} from "../../services/context/ProgramDataContext.js";
import { ModalProgramsManagement } from "./viewComponents/ProgramsManagement";

// Panels
import CompanyPanel from "./viewComponents/CompanyPanel/CompanyPanel.js";
import ChannelsPanel from "./viewComponents/ChannelsPanel/ChannelsPanel.js";
import MarketPanel from "./viewComponents/MarketPanel/MarketPanel.js";

// Styles following Inputs pattern with program selector
const executiveStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),

  cardHeaderCard: {
    marginTop: theme.spacing(-2),
    marginBottom: theme.spacing(-1.5),
  },

  cardHeaderTitle: {
    ...theme.typography.h6,
    fontSize: "1.125rem",
    color: theme.palette.secondary.main,
    fontWeight: "400",
    marginBottom: "10px",
    marginTop: "10px",
  },

  headerRight: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    top: "50%",
    marginTop: "-12px",
    display: "flex",
    alignItems: "center",
    minWidth: "300px",
    [theme.breakpoints.down("sm")]: {
      position: "static",
      transform: "none",
      marginTop: theme.spacing(1),
      width: "100%",
      minWidth: "unset",
    },
  },

  programSelectorLabel: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    whiteSpace: "nowrap",
  },

  programSelectFormControl: {
    minWidth: 220,
    flex: 1,
    margin: "0 !important",
    "& .MuiSelect-root": {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: theme.palette.divider + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },

  programMenuItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(1, 2),
  },

  programMenuItemName: {
    fontWeight: 500,
  },

  programMenuItemCounts: {
    display: "flex",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },

  scenarioCountChip: {
    height: 18,
    fontSize: "0.65rem",
    "& .MuiChip-label": {
      padding: "0 6px",
    },
  },

  manageProgramsButton: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      color: clientColor,
    },
  },

  mainContainer: {
    marginTop: theme.spacing(0),
    // Precise calc: 100vh - navbar(70px) - content.marginTop(70px) - content.paddingTop(30px) - extra guarantee (15px)
    height: "calc(100vh - 185px)",
  },
  gridItem: {
    height: "100%",
  },
});

// Component with context access
const ExecutiveMainContent = () => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const useStyles = makeStyles((theme) => executiveStyle(theme, clientColor));
  const classes = useStyles();

  const T_PREFIX = "rgm.executive";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // Program context
  const {
    programs,
    selectedProgramId,
    selectProgram,
    isLoading,
    refreshPrograms,
  } = useProgramDataContext();

  // Modal state
  const [isProgramsModalOpen, setIsProgramsModalOpen] = useState(false);

  // Filters (future: will be managed by Program)
  const filters = {
    products: [],
    brands: [],
    channels: [],
    regions: [],
    clients: [],
  };

  // Handler
  const handleProgramChange = (event) => {
    selectProgram(event.target.value);
  };

  // Render scenario count chips
  const renderScenarioCounts = (program) => {
    const counts = program.scenario_counts || {};
    return (
      <Box className={classes.programMenuItemCounts}>
        {counts.actual > 0 && (
          <Chip
            label={`A: ${counts.actual}`}
            size="small"
            className={classes.scenarioCountChip}
            style={{ backgroundColor: "#4caf50", color: "white" }}
          />
        )}
        {counts.forecast > 0 && (
          <Chip
            label={`F: ${counts.forecast}`}
            size="small"
            className={classes.scenarioCountChip}
            style={{ backgroundColor: "#2196f3", color: "white" }}
          />
        )}
        {counts.policy > 0 && (
          <Chip
            label={`P: ${counts.policy}`}
            size="small"
            className={classes.scenarioCountChip}
            style={{ backgroundColor: "#ff9800", color: "white" }}
          />
        )}
      </Box>
    );
  };

  return (
    <div>
      {/* Header Card - With Program Selector */}
      <Card className={classes.cardHeaderCard}>
        <CardHeader color="client" icon>
          <CardIcon color="client">
            <VisibilityIcon />
          </CardIcon>
          <h4 className={classes.cardHeaderTitle}>{t("title")}</h4>

          {/* Centered Program Selector */}
          <Box className={classes.headerRight}>
            <Typography className={classes.programSelectorLabel}>
              {t("program_selector.label", "Program:")}
            </Typography>
            <FormControl
              className={classes.programSelectFormControl}
              size="small"
            >
              <Select
                value={selectedProgramId || ""}
                onChange={handleProgramChange}
                displayEmpty
                disabled={programs.length === 0 || isLoading}
                renderValue={(selected) => {
                  if (!selected || selected === "") {
                    return (
                      <em>
                        {t("program_selector.placeholder", "Select a program")}
                      </em>
                    );
                  }
                  const program = programs.find(
                    (p) => p.program_id === selected,
                  );
                  return program?.program_name || selected;
                }}
                MenuProps={{
                  className: classes.selectMenu,
                  classes: { paper: classes.selectPaper },
                }}
              >
                {programs.length === 0 ? (
                  <MenuItem
                    value=""
                    disabled
                    className={classes.selectMenuItem}
                  >
                    <em>
                      {t(
                        "program_selector.no_programs",
                        "No programs available",
                      )}
                    </em>
                  </MenuItem>
                ) : (
                  programs.map((program) => (
                    <MenuItem
                      key={program.program_id}
                      value={program.program_id}
                      className={classes.programMenuItem}
                    >
                      <Typography className={classes.programMenuItemName}>
                        {program.program_name}
                      </Typography>
                      {renderScenarioCounts(program)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Manage Programs Button */}
            <Tooltip title={t("program_selector.manage", "Manage Programs")}>
              <IconButton
                className={classes.manageProgramsButton}
                onClick={() => setIsProgramsModalOpen(true)}
                size="small"
              >
                <LayersIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardHeader>
      </Card>

      {/* Programs Management Modal */}
      <ModalProgramsManagement
        open={isProgramsModalOpen}
        onClose={() => setIsProgramsModalOpen(false)}
        onProgramsChange={refreshPrograms}
      />

      {/* Main Content - 3 Panels */}
      <GridContainer className={classes.mainContainer}>
        <GridItem xs={12} sm={12} md={4} className={classes.gridItem}>
          <CompanyPanel programId={selectedProgramId} filters={filters} />
        </GridItem>
        <GridItem xs={12} sm={12} md={4} className={classes.gridItem}>
          <ChannelsPanel programId={selectedProgramId} filters={filters} />
        </GridItem>
        <GridItem xs={12} sm={12} md={4} className={classes.gridItem}>
          <MarketPanel programId={selectedProgramId} filters={filters} />
        </GridItem>
      </GridContainer>
    </div>
  );
};

// Main component with provider
export default function ExecutiveMain() {
  return (
    <ProgramDataProvider>
      <ExecutiveMainContent />
    </ProgramDataProvider>
  );
}
